import React, { useMemo, useEffect, useState, useRef } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
  ResponsiveContainer,
} from "recharts";
import { useNavigate } from "react-router";
import ArthaMultiSelect from "../../fields/ArthaMultiSelect";
import { MultiSelect } from "primereact/multiselect";
import {
  fetchOrganizationUsers,
  FiltertrackingDataApi,
} from "../../actioncreators/actioncreators";

const TrackedHoursCard = ({
  trackedHoursData,
  totalWorkedHours,
  totalBreakHours,
  locations,
  totalOvertimeHours,
  globalLocationFilter,
}) => {
  console.log("trackedHoursData", trackedHoursData);
  console.log("totalOvertimeHours", totalOvertimeHours);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState([{ name: "Week", code: "week" }]);
  const [members, setMembers] = useState([]);
  const [selectedMemberIds, setSelectedMemberIds] = useState([]);
  const [periodType, setPeriodType] = useState("week"); // default to week
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [currentWeekOffset, setCurrentWeekOffset] = useState(0); // 0 = current week, -1 = previous week, etc.
  const [currentMonthOffset, setCurrentMonthOffset] = useState(0); // 0 = current month, -1 = previous month, etc.
  const [totalTrackedHoursForChart, setTotalTrackedHoursForChart] = useState([
    { day: "Mon", Worked: 0, Breaks: 0, Overtime: 0 },
    { day: "Tue", Worked: 0, Breaks: 0, Overtime: 0 },
    { day: "Wed", Worked: 0, Breaks: 0, Overtime: 0 },
    { day: "Thu", Worked: 0, Breaks: 0, Overtime: 0 },
    { day: "Fri", Worked: 0, Breaks: 0, Overtime: 0 },
    { day: "Sat", Worked: 0, Breaks: 0, Overtime: 0 },
    { day: "Sun", Worked: 0, Breaks: 0, Overtime: 0 },
  ]);
  const [totalWorkedHoursForChart, setTotalWorkedHoursForChart] = useState(totalWorkedHours);
  const [totalBreakedHoursForChart, setTotalBreakedHoursForChart] = useState(totalBreakHours);
  const [totalOvertimeHoursForChart, setTotalOvertimeHoursForChart] = useState(totalOvertimeHours);
  const periodOptions = [
    { name: "Week", code: "week" },
    { name: "Month", code: "month" },
  ];
  const [isPeriodDropdownOpen, setIsPeriodDropdownOpen] = useState(false);
  const periodDropdownRef = useRef(null);
  const periodMultiSelectRef = useRef(null);

  useEffect(() => {
    setTotalTrackedHoursForChart(trackedHoursData);
    setTotalWorkedHoursForChart(totalWorkedHours);
    setTotalBreakedHoursForChart(totalBreakHours);
    setTotalOvertimeHoursForChart(totalOvertimeHours);

  }, [trackedHoursData, totalWorkedHours, totalBreakHours, totalOvertimeHours]);

  console.log("locations", locations);
  const infoItems = [
    {
      label: "Worked Hours",
      value: totalWorkedHoursForChart || "0h 0m",
      color: "text-green-1000",
    },
    {
      label: "Breaks",
      value: totalBreakedHoursForChart || "0h 0m",
      color: "text-yellow-1000",
    },
    {
      label: "Overtime",
      value: totalOvertimeHoursForChart || "0h 0m",
      color: "text-red-1000",
    },
  ];

  // Initialize with current week data on component mount
  useEffect(() => {
    const { fromDate: from, toDate: to } = getWeekDateRange(0);
    setFromDate(from);
    setToDate(to);
  }, []);

  useEffect(() => {
    fetchMembers();
  }, [globalLocationFilter]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        periodDropdownRef.current &&
        !periodDropdownRef.current.contains(event.target)
      ) {
        setIsPeriodDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const fetchMembers = async () => {
    try {
      // Pass global location filter to fetch only users from that location
      const response = await fetchOrganizationUsers(globalLocationFilter);

      // Debug: log the full response
      console.log('fetchOrganizationUsers response:', response);

      // fetchOrganizationUsers already returns response.data, so the structure is:
      // { businessStatusCode: 2, data: [...] }
      const apiData = response?.data || [];
      const businessStatusCode = response?.businessStatusCode;

      // Debug: log extracted data
      console.log('Extracted user data:', apiData);
      console.log('Business status code:', businessStatusCode);

      if (businessStatusCode === 2 && Array.isArray(apiData)) {
        setMembers(apiData);
        // Reset selected members when location filter changes
        setSelectedMembers([]);
        setSelectedMemberIds([]);
      } else {
        console.error("No members found or invalid response. BusinessStatusCode:", businessStatusCode);
        console.error("API Data:", apiData);
        // Set empty array to prevent errors
        setMembers([]);
        setSelectedMembers([]);
        setSelectedMemberIds([]);
      }
    } catch (err) {
      console.error("Error fetching members:", err);
      console.error("Error details:", err.message);
      // Set empty array to prevent errors
      setMembers([]);
      setSelectedMembers([]);
      setSelectedMemberIds([]);
    }
  };

  const convertWorkedTimeToHours = (workedTime) => {
    const [hours, minutes] = workedTime.match(/\d+/g).map(Number);
    return hours + minutes / 60;
  };

  // Helper function to get week date range
  const getWeekDateRange = (offset = 0) => {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const monday = new Date(
      today.setDate(today.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1))
    );
    // Apply offset (in weeks)
    monday.setDate(monday.getDate() + (offset * 7));

    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);

    return {
      fromDate: formatDateForAPI(monday),
      toDate: formatDateForAPI(sunday)
    };
  };

  // Helper function to get month date range
  const getMonthDateRange = (offset = 0) => {
    const today = new Date();
    const month = today.getMonth() + offset;
    const year = today.getFullYear() + Math.floor(month / 12);
    const adjustedMonth = ((month % 12) + 12) % 12;

    const firstDay = new Date(year, adjustedMonth, 1);
    const lastDay = new Date(year, adjustedMonth + 1, 0);

    return {
      fromDate: formatDateForAPI(firstDay),
      toDate: formatDateForAPI(lastDay)
    };
  };

  // Helper function to format date as YYYY-MM-DD
  const formatDateForAPI = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Helper function to generate chart labels based on period type
  const generateChartLabels = (periodType, fromDate, toDate) => {
    if (periodType === 'week') {
      return [
        { day: "Mon", Worked: 0, Breaks: 0, Overtime: 0 },
        { day: "Tue", Worked: 0, Breaks: 0, Overtime: 0 },
        { day: "Wed", Worked: 0, Breaks: 0, Overtime: 0 },
        { day: "Thu", Worked: 0, Breaks: 0, Overtime: 0 },
        { day: "Fri", Worked: 0, Breaks: 0, Overtime: 0 },
        { day: "Sat", Worked: 0, Breaks: 0, Overtime: 0 },
        { day: "Sun", Worked: 0, Breaks: 0, Overtime: 0 },
      ];
    } else if (periodType === 'month') {
      const start = new Date(fromDate);
      const end = new Date(toDate);
      const days = [];

      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        days.push({
          day: String(d.getDate()).padStart(2, '0'),
          Worked: 0,
          Breaks: 0,
          Overtime: 0
        });
      }
      return days;
    }
    return [];
  };

  const fetchFilteredData = async () => {
    // Use global location filter if set, otherwise empty array
    const locationIds = globalLocationFilter ? [globalLocationFilter] : [];

    const payload = {
      userId: selectedMemberIds,
      locationId: locationIds,
      fromDate: fromDate,
      toDate: toDate,
    };

    try {
      const response = await FiltertrackingDataApi(payload);

      console.log("API response:", response);
      console.log("Payload sent:", payload);

      if (response.businessStatusCode === 2 && response?.dashboardData) {
        const trackingData = response?.dashboardData?.trackingData;
        console.log("Tracking data received:", trackingData);

        // Generate fresh chart structure based on current period
        const chartLabels = generateChartLabels(periodType, fromDate, toDate);

        // Map the API response data to the chart labels
        const updatedTrackedHoursData = chartLabels.map((entry, index) => {
          const workData = trackingData && trackingData[index];

          if (workData && workData.workedTime) {
            return {
              ...entry,
              Worked: convertWorkedTimeToHours(workData.workedTime),
              Breaks: convertWorkedTimeToHours(workData.breakTime || "0h 0m"),
              Overtime: convertWorkedTimeToHours(workData.overTime || "0h 0m"),
            };
          }
          return entry;
        });

        setTotalWorkedHoursForChart(response?.dashboardData?.totalWorkedTime || "0h 0m");
        setTotalBreakedHoursForChart(response?.dashboardData?.totalBreakTime || "0h 0m");
        setTotalOvertimeHoursForChart(response?.dashboardData?.totalOverTime || "0h 0m");
        setTotalTrackedHoursForChart(updatedTrackedHoursData);

      } else {
        console.error("No data found or invalid response");
        // Reset to empty chart on error
        const chartLabels = generateChartLabels(periodType, fromDate, toDate);
        setTotalTrackedHoursForChart(chartLabels);
      }
    } catch (err) {
      console.error("Error fetching filtered data:", err);
      // Reset to empty chart on error
      const chartLabels = generateChartLabels(periodType, fromDate, toDate);
      setTotalTrackedHoursForChart(chartLabels);
    }
  };

  // Update date range when period type or offsets change
  useEffect(() => {
    if (periodType === 'week') {
      const { fromDate: from, toDate: to } = getWeekDateRange(currentWeekOffset);
      setFromDate(from);
      setToDate(to);
    } else if (periodType === 'month') {
      const { fromDate: from, toDate: to } = getMonthDateRange(currentMonthOffset);
      setFromDate(from);
      setToDate(to);
    }
  }, [periodType, currentWeekOffset, currentMonthOffset]);

  // Call API whenever dates change (with or without filters)
  useEffect(() => {
    if (fromDate && toDate && periodType) {
      console.log(`Calling API with date range: ${fromDate} to ${toDate}`);
      fetchFilteredData();
    }
  }, [fromDate, toDate, selectedMemberIds, globalLocationFilter]);

  const getCurrentPeriodDates = () => {
    if (!fromDate || !toDate) return [];

    const start = new Date(fromDate);
    const end = new Date(toDate);
    const dates = [];

    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      const day = String(d.getDate()).padStart(2, "0");
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const year = d.getFullYear();
      dates.push(`${day}/${month}/${year}`);
    }

    return dates;
  };

  const periodDates = useMemo(() => getCurrentPeriodDates(), [fromDate, toDate]);

  const dataWithDates = totalTrackedHoursForChart.map((data, index) => ({
    ...data,
    date: periodDates[index] || "",
  }));

  const timeFormatter = (value) => {
    const hours = Math.floor(value);
    const minutes = Math.round((value - hours) * 60);
    return `${hours}h ${minutes > 0 ? `${minutes}m` : ""}`;
  };

  const memberOptions =
    members?.map((member) => ({
      name: member.full_name && member.full_name.trim() !== "" && member.full_name !== "null"
        ? member.full_name
        : member.user_id,
      code: member.user_id,
    })) || [];

  const handleMemberChange = (selected) => {
    setSelectedMembers(selected);
    setSelectedMemberIds(selected.map((member) => member.code));
  };

  const handlePeriodChange = (newValue) => {
    // Case: user clicked the already-selected option (Week or Month)
    // MultiSelect tries to clear the selection (newValue = []),
    // but we want to KEEP the previous selection.
    if (newValue.length === 0 && selectedPeriod.length > 0) {
      return; // ignore this change
    }

    const last = newValue[newValue.length - 1] || null;
    const finalValue = last ? [last] : [];

    setSelectedPeriod(finalValue);
    setPeriodType(last ? last.code : "");

    // Reset offsets when switching period types
    setCurrentWeekOffset(0);
    setCurrentMonthOffset(0);

    // Close the dropdown after selection (optional, if you added the ref)
    if (periodMultiSelectRef.current) {
      periodMultiSelectRef.current.hide();
    }
  };

  // Navigation handlers
  const handlePrevious = () => {
    if (periodType === 'week') {
      setCurrentWeekOffset(prev => prev - 1);
    } else if (periodType === 'month') {
      setCurrentMonthOffset(prev => prev - 1);
    }
  };

  const handleNext = () => {
    if (periodType === 'week') {
      setCurrentWeekOffset(prev => prev + 1);
    } else if (periodType === 'month') {
      setCurrentMonthOffset(prev => prev + 1);
    }
  };

  // Helper to get display text for current period
  const getCurrentPeriodText = () => {
    if (!periodType) return '';

    if (periodType === 'week') {
      const { fromDate: from, toDate: to } = getWeekDateRange(currentWeekOffset);
      const fromDateObj = new Date(from);
      const toDateObj = new Date(to);

      const fromMonth = fromDateObj.toLocaleString('en-US', { month: 'short' });
      const fromDay = fromDateObj.getDate();
      const toMonth = toDateObj.toLocaleString('en-US', { month: 'short' });
      const toDay = toDateObj.getDate();

      // Format: "Nov 28 - Dec 4" (always show both months)
      return `${fromMonth} ${fromDay} - ${toMonth} ${toDay}`;
    } else if (periodType === 'month') {
      const { fromDate: from } = getMonthDateRange(currentMonthOffset);
      const date = new Date(from);
      return date.toLocaleString('en-US', { month: 'long', year: 'numeric' });
    }
    return '';
  };




  return (
    <>
      <style>
        {`
        /* Remove header (X area) completely */
        .period-panel .p-multiselect-header {
          margin: 0 !important;
          padding: 0 !important;
          border: 0 !important;
          height: 0 !important;
          min-height: 0 !important;
          overflow: hidden !important;
        }

        /* Remove padding so there is no blank space top/bottom */
        .period-panel {
          padding: 0 !important;
        }

        .period-panel .p-multiselect-items-wrapper {
          max-height: none !important;
          height: auto !important;
          overflow: hidden !important;
          padding: 0 !important;
        }

        .period-panel .p-multiselect-items {
          padding: 0 !important;
          margin: 0 !important;
        }

        .period-panel .p-multiselect-item {
          margin: 0 !important;
          font-size: 10px !important;
          padding: 4px 8px !important;
        }

        /* Optional: hide checkbox */
        .period-panel .p-checkbox {
          display: none !important;
        }

        /* Dropdown panel options font size */
        .p-multiselect-panel .p-multiselect-item {
          font-size: 10px !important;
          padding: 4px 8px !important;
        }

        .p-multiselect-panel .p-multiselect-item .p-checkbox {
          width: 14px !important;
          height: 14px !important;
        }

        .p-multiselect-panel .p-checkbox .p-checkbox-box {
          width: 14px !important;
          height: 14px !important;
          border: 1px solid #d1d5db !important;
          border-radius: 3px !important;
        }

        .p-multiselect-panel .p-checkbox .p-checkbox-box.p-highlight {
          border-color: #3b82f6 !important;
          background: #3b82f6 !important;
        }

        .p-multiselect-panel .p-checkbox .p-checkbox-icon {
          font-size: 9px !important;
        }

        /* Reduce search box size */
        .p-multiselect-panel .p-multiselect-header {
          padding: 4px 8px !important;
        }

        .p-multiselect-panel .p-multiselect-filter-container {
          margin: 0 !important;
        }

        .p-multiselect-panel .p-multiselect-filter {
          font-size: 10px !important;
          padding: 4px 8px !important;
          height: 24px !important;
        }

        .p-multiselect-panel .p-multiselect-filter-icon {
          font-size: 10px !important;
          right: 8px !important;
        }

        /* Professional Chart Styles */
        .tracked-hours-card {
          border: 1px solid #e5e7eb;
          border-radius: 10px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06);
          transition: box-shadow 0.3s ease;
          background: #ffffff;
        }

        .tracked-hours-card:hover {
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.06);
        }

        .chart-header {
          padding: 12px 16px;
          border-bottom: 1px solid #f3f4f6;
          background: #ffffff;
        }

        .chart-title {
          font-size: 13px;
          font-weight: 600;
          color: #1f2937;
          margin: 0;
        }

        .chart-filters {
          display: flex;
          gap: 6px;
          align-items: center;
          flex-wrap: wrap;
        }

        .period-nav-btn {
          background: #ffffff;
          border: 1px solid #d1d5db;
          border-radius: 5px;
          padding: 3px 8px;
          font-size: 10px;
          font-weight: 1000;
          color: #6b7280;
          cursor: pointer;
          transition: all 0.2s ease;
          min-width: 24px;
          height: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .period-nav-btn:hover:not(:disabled) {
          background: #f9fafb;
          border-color: #9ca3af;
          color: #374151;
        }

        .period-nav-btn:disabled {
          opacity: 0.4;
          cursor: not-allowed;
        }

        .period-date-display {
          font-size: 10px;
          font-weight: 1000;
          color: #4b5563;
          min-width: 115px;
          text-align: center;
          padding: 0 6px;
        }

        .chart-content {
          padding: 14px 16px;
          display: flex;
          gap: 18px;
          background: #ffffff;
        }

        .stats-sidebar {
          min-width: 140px;
          padding-right: 18px;
          border-right: 2px solid #f3f4f6;
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .stat-item {
          display: flex;
          flex-direction: column;
          gap: 3px;
          padding: 9px 10px;
          background: #ffffff;
          border-radius: 7px;
          transition: background 0.2s ease;
        }

        .stat-item:hover {
          background: #fafafa;
        }

        .stat-label {
          font-size: 9px;
          font-weight: 1000;
          color: #6b7280;
          text-transform: uppercase;
          letter-spacing: 0.4px;
          display: flex;
          align-items: center;
          gap: 5px;
        }

        .stat-indicator {
          width: 7px;
          height: 7px;
          border-radius: 50%;
          display: inline-block;
        }

        .stat-value {
          font-size: 15px;
          font-weight: 600;
          margin: 0;
        }

        .stat-worked .stat-value { color: #92D050; }
        .stat-worked .stat-indicator { background: #92D050; }
        
        .stat-breaks .stat-value { color: #FFC107; }
        .stat-breaks .stat-indicator { background: #FFC107; }
        
        .stat-overtime .stat-value { color: #F44250; }
        .stat-overtime .stat-indicator { background: #F44250; }

        .chart-area {
          flex: 1;
          min-width: 0;
        }

        /* Custom filter dropdowns */
        .custom-multiselect {
          height: 24px !important;
          font-size: 10px !important;
          border: 1px solid #d1d5db !important;
          border-radius: 5px !important;
          min-width: 120px !important;
        }

        .custom-multiselect .p-multiselect-label {
          padding: 3px 8px !important;
          font-size: 10px !important;
          line-height: 16px !important;
          display: flex !important;
          align-items: center !important;
        }

        .custom-multiselect .p-multiselect-trigger {
          width: 20px !important;
        }

        .period-multiselect {
          min-width: 100px !important;
        }

        .period-multiselect .p-multiselect-label {
          padding: 3px 6px !important;
        }

        /* Recharts customization */
        .recharts-cartesian-grid-horizontal line,
        .recharts-cartesian-grid-vertical line {
          stroke: #f3f4f6 !important;
        }

        .recharts-legend-wrapper {
          padding-top: 6px !important;
        }

        .recharts-legend-item-text {
          font-size: 10px !important;
          font-weight: 1000 !important;
          color: #6b7280 !important;
        }

        .recharts-tooltip-wrapper {
          outline: none !important;
        }

        .recharts-default-tooltip {
          background: rgba(255, 255, 255, 0.98) !important;
          border: 1px solid #e5e7eb !important;
          border-radius: 7px !important;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1) !important;
          padding: 6px 10px !important;
          font-size: 10px !important;
        }

        @media (max-width: 1200px) {
          .chart-filters {
            gap: 6px;
          }
          .custom-multiselect {
            min-width: 120px !important;
          }
        }

        @media (max-width: 768px) {
          .chart-content {
            flex-direction: column;
          }
          .stats-sidebar {
            flex-direction: row;
            border-right: none;
            border-bottom: 2px solid #f3f4f6;
            padding-bottom: 16px;
            padding-right: 0;
          }
        }
      `}
      </style>
      <div className="col-12 col-md-12 tracked-hours-card mb-3">
        <div className="chart-header d-flex justify-content-between align-items-center">
          <h3 className="chart-title">Tracked Hours Overview</h3>

          <div className="chart-filters">
            {/* Period Navigation */}
            {periodType && (
              <div className="d-flex align-items-center gap-1">
                <button
                  onClick={handlePrevious}
                  className="period-nav-btn"
                  title={`Previous ${periodType}`}
                >
                  ‹
                </button>
                <span className="period-date-display">
                  {getCurrentPeriodText()}
                </span>
                <button
                  onClick={handleNext}
                  className="period-nav-btn"
                  title={`Next ${periodType}`}
                  disabled={
                    (periodType === 'week' && currentWeekOffset >= 0) ||
                    (periodType === 'month' && currentMonthOffset >= 0)
                  }
                >
                  ›
                </button>
              </div>
            )}

            {/* Period Type Dropdown */}
            <MultiSelect
              ref={periodMultiSelectRef}
              value={selectedPeriod}
              options={periodOptions}
              onChange={(e) => handlePeriodChange(e.value)}
              optionLabel="name"
              filter={false}
              showSelectAll={false}
              placeholder="Period"
              className="custom-multiselect period-multiselect"
              panelClassName="period-panel"
            />

            <ArthaMultiSelect
              options={memberOptions}
              value={selectedMembers}
              onChange={handleMemberChange}
              placeholder="Users"
              className="custom-multiselect"
            />
          </div>
        </div>

        <div className="chart-content">
          <div className="stats-sidebar">
            <div className="stat-item stat-worked">
              <div className="stat-label">
                <span className="stat-indicator"></span>
                Worked Hours
              </div>
              <p className="stat-value">{totalWorkedHoursForChart || "0h 0m"}</p>
            </div>

            <div className="stat-item stat-breaks">
              <div className="stat-label">
                <span className="stat-indicator"></span>
                Break Time
              </div>
              <p className="stat-value">{totalBreakedHoursForChart || "0h 0m"}</p>
            </div>

            <div className="stat-item stat-overtime">
              <div className="stat-label">
                <span className="stat-indicator"></span>
                Overtime
              </div>
              <p className="stat-value">{totalOvertimeHoursForChart || "0h 0m"}</p>
            </div>
          </div>

          <div className="chart-area">
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={dataWithDates}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="day"
                  tick={{ fontSize: 10 }}
                  tickLine={false}
                />
                <YAxis
                  tickFormatter={timeFormatter}
                  tick={{ fontSize: 10 }}
                  tickLine={false}
                />
                <Tooltip
                  formatter={timeFormatter}
                  contentStyle={{
                    fontSize: "10px",
                    borderRadius: "7px",
                  }}
                />
                <Legend
                  wrapperStyle={{ fontSize: "10px" }}
                  iconSize={10}
                />
                <Bar dataKey="Worked" fill="#92D050" radius={[4, 4, 0, 0]} />
                <Bar dataKey="Breaks" fill="#FFC107" radius={[4, 4, 0, 0]} />
                <Bar dataKey="Overtime" fill="#F44250" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </>
  );
};

export default TrackedHoursCard;
